import React, { useEffect, useState, forwardRef } from 'react';
import {
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  useTheme,
  Box,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ShekelIcon from '../icons/ShekelIcon';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import { Icon } from '@iconify/react';

interface CustomTextFieldProps extends Omit<MuiTextFieldProps, 'variant'> {
  error?: boolean;
  helperText?: string;
  showPassword?: boolean;
  handleClickShowPassword?: () => void;
  turnOff?: boolean;
  contrast?: boolean;
  customVariant?: 'money' | 'password' | 'date' | 'search';
  value?: string;
}

const TextField = forwardRef<HTMLDivElement, CustomTextFieldProps>(({
  error,
  helperText,
  showPassword,
  handleClickShowPassword,
  turnOff = false,
  customVariant,
  contrast = true,
  value,
  sx,
  ...props
}, ref) => {
  const theme = useTheme();
  const borderColor = error ? '#E53E3E' : theme.palette.dark?.border;
  const textColor = turnOff
    ? theme.palette.grey[500]
    : theme.palette.dark?.main;

  const [formattedValue, setFormattedValue] = useState<string>(value || '');

  useEffect(() => {
    if (customVariant === 'money' && value) {
      const numericValue = value.replace(/[^\d]/g, '');
      if (numericValue) {
        const formatted = Number(numericValue).toLocaleString('he-IL');
        setFormattedValue(formatted);
      } else {
        setFormattedValue(value);
      }
    } else {
      setFormattedValue(value || '');
    }
  }, [value, customVariant]);

  const combinedSx = {
    borderRadius: 4,
    width: '100%',
    '& .MuiFilledInput-root': {
      backgroundColor: contrast ? '#F2F4F7' : 'transparent',
      borderRadius: 4,
      border: `1px solid ${borderColor}`,
      '&:hover': {
        backgroundColor: contrast ? '#F2F4F7' : 'transparent',
        borderColor: borderColor,
      },
      '&.Mui-focused': {
        backgroundColor: contrast ? '#F2F4F7' : 'transparent',
        borderColor: borderColor,
      },
      '&.Mui-disabled': {
        backgroundColor: contrast ? '#F2F4F7' : 'transparent',
      },
    },
    '& .MuiInputBase-input': {
      color: textColor,
      WebkitTextFillColor: textColor,
      textAlign: 'right',
      direction: 'rtl',
      fontFamily: 'AlmoniDL',
      fontWeight: 700,
      '&:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 1000px transparent inset',
        WebkitTextFillColor: textColor,
        transition: 'background-color 5000s ease-in-out 0s',
        backgroundColor: contrast
          ? '#F2F4F7 !important'
          : 'transparent !important',
      },
      '&:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
        {
          WebkitBoxShadow: '0 0 0 1000px transparent inset',
          WebkitTextFillColor: textColor,
          backgroundColor: contrast
            ? '#F2F4F7 !important'
            : 'transparent !important',
        },
    },
    '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:hover:before, & .MuiFilledInput-underline:after':
      {
        borderBottomColor: 'transparent !important',
      },
    ...sx,
  };

  const moneyEndAdornment =
    customVariant === 'money' ? (
      <InputAdornment position="end">
        <Box
          sx={{
            background: '#343434',
            p: '15px',
            borderRadius: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon
            icon="fa-solid:shekel-sign"
            color={'#C1FE40'}
            width="16px"
            height="16px"
          />
        </Box>
      </InputAdornment>
    ) : null;

  const passwordEndAdornment =
    customVariant === 'password' ? (
      <InputAdornment position="end">
        <IconButton onClick={handleClickShowPassword} edge="end">
          {showPassword ? <Visibility /> : <VisibilityOff />}
        </IconButton>
      </InputAdornment>
    ) : null;

  const dateEndAdornment =
    customVariant === 'date' ? (
      <InputAdornment position="end">
        <IconButton edge="end">
          <CalendarTodayIcon />
        </IconButton>
      </InputAdornment>
    ) : null;

  const searchEndAdornment =
    customVariant === 'search' ? (
      <InputAdornment position="end">
        <IconButton edge="end">
          <SearchIcon />
        </IconButton>
      </InputAdornment>
    ) : null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (customVariant === 'money') {
      const inputValue = e.target.value;
      const numericValue = inputValue.replace(/[^\d]/g, '');

      if (numericValue) {
        const formatted = Number(numericValue).toLocaleString('he-IL');
        setFormattedValue(formatted);

        if (props.onChange) {
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              value: numericValue,
            },
          };
          props.onChange(syntheticEvent as any);
        }
      } else {
        setFormattedValue(inputValue);
        if (props.onChange) {
          props.onChange(e);
        }
      }
    } else if (props.onChange) {
      props.onChange(e);
    }
  };

  return (
    <Box ref={ref} sx={{ position: 'relative', width: '100%', direction: 'rtl' }}>
      <MuiTextField
        type={
          customVariant === 'password'
            ? showPassword
              ? 'text'
              : 'password'
            : 'text'
        }
        variant="filled"
        InputProps={{
          disableUnderline: true,
          endAdornment:
            moneyEndAdornment ||
            passwordEndAdornment ||
            dateEndAdornment ||
            searchEndAdornment,
          style: { color: textColor },
        }}
        InputLabelProps={{
          sx: {
            color: theme.palette.text.primary,
            fontFamily: 'AlmoniDL',
            fontWeight: 700,
            textAlign: 'right',
            right: 18,
            left: 'auto',
            transformOrigin: 'top right',
            direction: 'rtl',
            '&.Mui-focused': { color: 'black' },
          },
        }}
        value={customVariant === 'money' ? formattedValue : value}
        onChange={handleChange}
        sx={combinedSx}
        disabled={turnOff}
        error={error}
        fullWidth
        {...props}
      />
      {error && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: '8px' }}>
          <ErrorOutlineIcon
            sx={{
              color: '#E53E3E',
              mr: '5px',
              height: '14px',
              width: '14px',
            }}
          />
          <Typography
            variant="body1"
            fontWeight="fontWeightRegular"
            fontSize={14}
            sx={{ color: '#E53E3E' }}
          >
            {helperText}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

TextField.displayName = 'TextField';

export default TextField;
